// Console logger that captures browser logs and sends them to server.
// Off by default; enable with ?telemetryOn=true
class ConsoleLogger {
  private logs: Array<{
    timestamp: string;
    level: string;
    message: string;
    stack?: string;
  }> = [];

  private readonly batchSize = 20;
  private readonly flushIntervalMs = 5000; // 5 seconds
  private flushTimer?: ReturnType<typeof setInterval>;
  private inFlush = false;

  private readonly original = {
    log: console.log,
    error: console.error,
    warn: console.warn,
    info: console.info,
    debug: console.debug,
  };

  constructor() {
    if (!ConsoleLogger.isTelemetryEnabled()) return;
    this.setupConsoleHooks();
    this.setupErrorHandlers();
    this.startFlushTimer();
  }

  private static isTelemetryEnabled(): boolean {
    try {
      const url = new URL(window.location.href);
      const v = url.searchParams.get('telemetryOn');
      return v === 'true' || v === '1' || v === 'yes';
    } catch {
      return false;
    }
  }

  private setupConsoleHooks() {
    console.log = (...args) => {
      this.addLog('log', args.map((arg) => this.formatArg(arg)).join(' '));
      this.original.log.apply(console, args);
    };

    console.error = (...args) => {
      this.addLog('error', args.map((arg) => this.formatArg(arg)).join(' '));
      this.original.error.apply(console, args);
    };

    console.warn = (...args) => {
      this.addLog('warn', args.map((arg) => this.formatArg(arg)).join(' '));
      this.original.warn.apply(console, args);
    };

    console.info = (...args) => {
      this.addLog('info', args.map((arg) => this.formatArg(arg)).join(' '));
      this.original.info.apply(console, args);
    };

    console.debug = (...args) => {
      this.addLog('debug', args.map((arg) => this.formatArg(arg)).join(' '));
      this.original.debug.apply(console, args);
    };
  }

  private setupErrorHandlers() {
    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.addLog('error', `Unhandled Error: ${event.message}`, event.error?.stack);
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.addLog('error', `Unhandled Promise Rejection: ${event.reason}`, 
        event.reason?.stack || String(event.reason));
    });
  }

  private formatArg(arg: any): string {
    if (typeof arg === 'object') {
      try {
        const json = JSON.stringify(arg);
        return json.length > 4000 ? json.slice(0, 4000) + '…' : json;
      } catch (e) {
        return String(arg);
      }
    }
    const s = String(arg);
    return s.length > 2000 ? s.slice(0, 2000) + '…' : s;
  }

  private addLog(level: string, message: string, stack?: string) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...(stack && { stack: String(stack).slice(0, 8000) })
    };

    this.logs.push(logEntry);

    // Auto-flush if batch size reached
    if (this.logs.length >= this.batchSize) void this.flush();
  }

  private startFlushTimer() {
    this.flushTimer = setInterval(() => {
      if (this.logs.length > 0) {
        void this.flush();
      }
    }, this.flushIntervalMs);
  }

  private async flush() {
    if (this.inFlush) return;
    if (this.logs.length === 0) return;

    this.inFlush = true;

    const logsToSend = [...this.logs];
    this.logs = [];

    try {
      await fetch('/api/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          logs: logsToSend,
          userAgent: navigator.userAgent,
          url: window.location.href,
          sessionId: this.getSessionId()
        })
      });
    } catch (error) {
      // If sending fails, add logs back to queue (but don't recurse)
      this.original.error('Failed to send logs to server:', error);
      this.logs.unshift(...logsToSend);
    } finally {
      this.inFlush = false;
    }
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('debugSessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('debugSessionId', sessionId);
    }
    return sessionId;
  }

  // Manual flush method
  public flushNow() {
    this.flush();
  }

  // Cleanup method
  public destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushNow();
  }
}

// Auto-initialize if in browser environment
let logger: ConsoleLogger | null = null;

if (typeof window !== 'undefined') {
  logger = new ConsoleLogger();
  
  // Flush logs before page unload
  window.addEventListener('beforeunload', () => {
    logger?.flushNow();
  });
}

export default logger;